import React from 'react'

/**
 * Google Maps API Loader Utility
 *
 * This utility ensures the Google Maps API is loaded only once and provides
 * a promise-based interface for components that need to use Google Maps.
 *
 * This prevents the "gJ" error that occurs when the Google Maps API is loaded
 * multiple times or when components try to use it before it's fully loaded.
 */

interface GoogleMapsLoaderOptions {
    key: string
    libraries?: string[]
    version?: string
    language?: string
    region?: string
}

class GoogleMapsLoader {
    private static instance: GoogleMapsLoader
    private loadPromise: Promise<typeof google> | null = null
    private isLoaded = false

    private constructor() {}

    public static getInstance(): GoogleMapsLoader {
        if (!GoogleMapsLoader.instance) {
            GoogleMapsLoader.instance = new GoogleMapsLoader()
        }
        return GoogleMapsLoader.instance
    }

    /**
     * Load the Google Maps API if not already loaded
     */
    public async load(
        options: GoogleMapsLoaderOptions,
    ): Promise<typeof google> {
        // If already loaded, return the global google object
        if (this.isLoaded && typeof window !== 'undefined' && window.google) {
            return window.google
        }

        // If currently loading, return the existing promise
        if (this.loadPromise) {
            return this.loadPromise
        }

        // Start loading
        this.loadPromise = this.loadScript(options)

        try {
            const google = await this.loadPromise
            this.isLoaded = true
            return google
        } catch (error) {
            // Reset promise on error so we can retry
            this.loadPromise = null
            throw error
        }
    }

    private loadScript(
        options: GoogleMapsLoaderOptions,
    ): Promise<typeof google> {
        return new Promise((resolve, reject) => {
            // Check if we're in a browser environment
            if (typeof window === 'undefined') {
                reject(
                    new Error(
                        'Google Maps can only be loaded in browser environment',
                    ),
                )
                return
            }

            // Check if already loaded
            if (window.google && window.google.maps) {
                resolve(window.google)
                return
            }

            // Check if script is already in DOM (prevent duplicate loading)
            const existingScript = document.querySelector(
                '#google-maps-api-script, script[src*="maps.googleapis.com"]',
            )
            if (existingScript) {
                console.warn(
                    'Google Maps script already exists, waiting for it to load',
                )
                // Script exists, wait for it to load
                const checkLoaded = () => {
                    if (window.google && window.google.maps) {
                        resolve(window.google)
                    } else {
                        setTimeout(checkLoaded, 100)
                    }
                }
                checkLoaded()
                return
            }

            // Create and configure script element
            const script = document.createElement('script')
            script.type = 'text/javascript'
            script.async = true
            script.defer = true
            script.id = 'google-maps-api-script' // Add unique identifier

            // Build URL with parameters
            const params = new URLSearchParams({
                key: options.key,
                libraries: (options.libraries || ['places']).join(','),
                v: options.version || 'weekly',
                ...(options.language && { language: options.language }),
                ...(options.region && { region: options.region }),
            })

            script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`

            // Set up event handlers
            script.onload = () => {
                if (window.google && window.google.maps) {
                    resolve(window.google)
                } else {
                    reject(new Error('Google Maps API failed to load properly'))
                }
            }

            script.onerror = error => {
                reject(new Error(`Failed to load Google Maps API: ${error}`))
            }

            // Add script to document
            document.head.appendChild(script)
        })
    }

    /**
     * Check if Google Maps API is currently loaded
     */
    public isGoogleMapsLoaded(): boolean {
        return (
            this.isLoaded &&
            typeof window !== 'undefined' &&
            !!window.google?.maps
        )
    }

    /**
     * Get the loaded Google Maps API (throws if not loaded)
     */
    public getGoogle(): typeof google {
        if (!this.isGoogleMapsLoaded()) {
            throw new Error('Google Maps API is not loaded. Call load() first.')
        }
        return window.google
    }
}

// Export singleton instance
export const googleMapsLoader = GoogleMapsLoader.getInstance()

// Convenience function for loading with default options
export const loadGoogleMaps = async (): Promise<typeof google> => {
    const apiKey = process.env.GATSBY_GCP_KEY

    if (!apiKey) {
        throw new Error('GATSBY_GCP_KEY environment variable is not set')
    }

    return googleMapsLoader.load({
        key: apiKey,
        libraries: ['places'],
    })
}
